// Quote Builder — "sent" stamp at the top of the quote.
//
// Steve, 2026-10-14: a sent quote could be edited with no record of what the
// customer saw or what the margin was at that moment (KOS-Q-0001-0WTC went out
// at £9,151.27 / 29.2% and was edited to £10,401.10 / 21.2%). The server now
// keeps an immutable snapshot at every send (migration 0098); this strip reads
// it back:
//
//   • in step   — "Customer portal shows the version sent 13 Oct, 13:50 ·
//                  £9,151.27 inc VAT · margin 29.2%"
//   • edited    — amber: the portal still shows the sent version, here is what
//                  it was and what the quote is now; send again to update it
//   • history   — every snapshot (sends, acceptances, status changes, PDF
//                  exports), with the as-sent PDF where one was captured
//
// The comparison is done server-side (GET …/sent-stamp) against the PG copy, so
// it is refreshed whenever an autosave lands (serverVersion changes) and after
// a send. It never blocks editing or sending — it only tells.
//
// Retailer-only: margin and trade figures appear here exactly as they already
// do in the totals panel. Nothing in this module reaches the customer.
// Stripe / commission / defraud: untouched; read-only DOM + GETs.

import type { QuoteStore } from "./state";
import type { PageContext } from "../../shell/types";
import { fmtGbp, svgIcon } from "./formatters";

interface SnapshotSummary {
  id: string;
  event: "sent" | "accepted" | "unapproved" | "status_change" | "pdf_customer" | "pdf_internal" | "baseline";
  at: number;
  sentAt: number;
  statusFrom: string | null;
  statusTo: string | null;
  totalIncVat: number | null;
  marginPct: number | null;
  markupPct: number | null;
  lineCount: number;
  hasPdf: boolean;
  /** Who the customer copy went to / belongs to (0099). Names only. */
  recipientName?: string | null;
  recipientSecondName?: string | null;
  /** Retailer user who sent / exported / changed status (0101). Older rows: none. */
  senderName?: string | null;
}

interface SentStamp {
  reference: string;
  sentAt: number | null;
  lastSent: SnapshotSummary | null;
  current: { totalIncVat: number; marginPct: number; markupPct: number | null; lineCount: number };
  editedSinceSent: boolean;
}

const DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/London",
});
const fmtWhen = (ms: number): string => DATE_FMT.format(new Date(ms));
const fmtPct = (n: number | null | undefined): string =>
  typeof n === "number" && Number.isFinite(n) ? `${n.toFixed(1)}%` : "—";
const fmtMoney = (n: number | null | undefined): string =>
  typeof n === "number" && Number.isFinite(n) ? fmtGbp(n) : "—";

const EVENT_LABEL: Record<SnapshotSummary["event"], string> = {
  sent: "Sent to customer",
  baseline: "Sent (recorded when version history started)",
  accepted: "Accepted by customer",
  unapproved: "Approval withdrawn",
  status_change: "Status changed",
  pdf_customer: "Customer PDF exported",
  pdf_internal: "Internal PDF exported",
};

// Rows that carry a customer copy, so naming who it went to means something.
const NAMED_EVENTS = new Set<SnapshotSummary["event"]>(["sent", "baseline", "accepted", "unapproved"]);

// Rows a retailer user caused, so naming who did it means something (0101).
// Baselines were written by the backfill, accept/withdraw by the customer.
const SENDER_EVENTS = new Set<SnapshotSummary["event"]>(["sent", "status_change", "pdf_customer", "pdf_internal"]);

function whoLine(r: SnapshotSummary): string {
  const parts: string[] = [];
  if (NAMED_EVENTS.has(r.event) && r.recipientName) {
    const prefix = r.event === "sent" || r.event === "baseline" ? "to" : "by";
    const second = r.recipientSecondName ? ` + ${esc(r.recipientSecondName)}` : "";
    parts.push(`${prefix} ${esc(r.recipientName)}${second}`);
  }
  if (SENDER_EVENTS.has(r.event) && r.senderName) parts.push(`by ${esc(r.senderName)}`);
  return parts.length ? `<span class="qb-sent-stamp__who">${parts.join(" · ")}</span>` : "";
}

const ICON_OK = "M20 6 9 17l-5-5";
const ICON_WARN = "M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z|M12 9v4|M12 17h.01";
const ICON_CLOCK = "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z|M12 7v5l3 2";

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function pdfHref(quoteId: string, snapId: string): string {
  return `/api/portal/quote-builder/${encodeURIComponent(quoteId)}/snapshots/${encodeURIComponent(snapId)}/pdf`;
}

export function mountSentStamp(host: HTMLElement, store: QuoteStore, ctx: PageContext): () => void {
  let destroyed = false;
  let seq = 0;
  let lastKey = "";
  let timer: number | null = null;
  const followUps: number[] = [];
  let historyOpen = false;
  let stamp: SentStamp | null = null;
  let stampQuoteId = "";

  host.classList.add("qb-sent-stamp-host");

  async function refresh(): Promise<void> {
    const q = store.get();
    const quoteId = q.serverId || "";
    const mySeq = ++seq;
    if (!quoteId) { stamp = null; stampQuoteId = ""; render(); return; }
    try {
      const res = await ctx.api<{ ok?: boolean; stamp?: SentStamp }>(
        `/portal/quote-builder/${encodeURIComponent(quoteId)}/sent-stamp`);
      if (destroyed || mySeq !== seq) return;
      stamp = res.stamp ?? null;
      stampQuoteId = quoteId;
    } catch {
      if (destroyed || mySeq !== seq) return;
      // A server without 0098 (404) or a blip: show nothing rather than guess.
      stamp = null;
      stampQuoteId = "";
    }
    render();
  }

  function schedule(delay = 600): void {
    if (timer !== null) window.clearTimeout(timer);
    timer = window.setTimeout(() => { timer = null; void refresh(); }, delay);
  }

  function render(): void {
    if (destroyed) return;
    const s = stamp;
    if (!s || !stampQuoteId) { host.innerHTML = ""; host.hidden = true; return; }
    const last = s.lastSent;
    if (!last && !s.sentAt) { host.innerHTML = ""; host.hidden = true; return; }
    host.hidden = false;

    let cls = "qb-sent-stamp";
    let icon = ICON_OK;
    let body = "";
    if (!last) {
      cls += " qb-sent-stamp--unknown";
      icon = ICON_CLOCK;
      body = `<strong>Sent ${esc(fmtWhen(s.sentAt as number))}.</strong>
        <span>This was sent before version history started, so the version the customer saw was not recorded. Their portal shows whatever was last sent.</span>`;
    } else if (s.editedSinceSent) {
      cls += " qb-sent-stamp--edited";
      icon = ICON_WARN;
      body = `<strong>Edited since sent.</strong>
        <span>The customer portal still shows the version sent ${esc(fmtWhen(last.sentAt))}:
          ${esc(fmtMoney(last.totalIncVat))} inc VAT, margin ${esc(fmtPct(last.marginPct))}.
          Now ${esc(fmtMoney(s.current.totalIncVat))}, margin ${esc(fmtPct(s.current.marginPct))}.
          Use <em>Send to customer</em> to update their copy.</span>`;
    } else {
      body = `<strong>Customer portal shows the version sent ${esc(fmtWhen(last.sentAt))}.</strong>
        <span>${esc(fmtMoney(last.totalIncVat))} inc VAT · margin ${esc(fmtPct(last.marginPct))}${last.markupPct != null ? ` · markup ${esc(fmtPct(last.markupPct))}` : ""}</span>`;
    }

    const links: string[] = [];
    if (last && last.hasPdf) {
      links.push(`<a class="qb-sent-stamp__link" href="${esc(pdfHref(stampQuoteId, last.id))}" target="_blank" rel="noopener">Sent PDF</a>`);
    }
    links.push(`<button type="button" class="qb-sent-stamp__link" data-role="history" aria-expanded="${historyOpen}">History</button>`);

    host.innerHTML = `
      <div class="${cls}" role="status">
        <div class="qb-sent-stamp__ic">${svgIcon(icon)}</div>
        <div class="qb-sent-stamp__tx">${body}</div>
        <div class="qb-sent-stamp__links">${links.join("")}</div>
      </div>
      <div class="qb-sent-stamp__history" data-role="history-list" ${historyOpen ? "" : "hidden"}></div>
    `;
    host.querySelector<HTMLButtonElement>("[data-role='history']")?.addEventListener("click", () => {
      historyOpen = !historyOpen;
      render();
      if (historyOpen) void loadHistory();
    });
    if (historyOpen) void loadHistory();
  }

  async function loadHistory(): Promise<void> {
    const list = host.querySelector<HTMLElement>("[data-role='history-list']");
    const quoteId = stampQuoteId;
    if (!list || !quoteId) return;
    list.innerHTML = `<div class="qb-sent-stamp__empty">Loading…</div>`;
    try {
      const res = await ctx.api<{ ok?: boolean; snapshots?: SnapshotSummary[] }>(
        `/portal/quote-builder/${encodeURIComponent(quoteId)}/snapshots`);
      if (destroyed || quoteId !== stampQuoteId) return;
      const rows = res.snapshots ?? [];
      const target = host.querySelector<HTMLElement>("[data-role='history-list']");
      if (!target) return;
      if (rows.length === 0) {
        target.innerHTML = `<div class="qb-sent-stamp__empty">No versions recorded yet.</div>`;
        return;
      }
      target.innerHTML = `
        <table class="qb-sent-stamp__table">
          <thead><tr><th>When</th><th>What</th><th class="num">Total inc VAT</th><th class="num">Margin</th><th class="num">Lines</th><th></th></tr></thead>
          <tbody>${rows.map((r) => {
            const what = r.event === "status_change" && r.statusTo
              ? `Status: ${esc(r.statusFrom || "?")} → ${esc(r.statusTo)}`
              : esc(EVENT_LABEL[r.event] || r.event);
            const when = r.event === "baseline" ? r.sentAt : r.at;
            return `<tr>
              <td>${esc(fmtWhen(when))}</td>
              <td>${what}${whoLine(r)}</td>
              <td class="num">${esc(fmtMoney(r.totalIncVat))}</td>
              <td class="num">${esc(fmtPct(r.marginPct))}</td>
              <td class="num">${r.lineCount}</td>
              <td>${r.hasPdf ? `<a href="${esc(pdfHref(quoteId, r.id))}" target="_blank" rel="noopener">PDF</a>` : ""}</td>
            </tr>`;
          }).join("")}</tbody>
        </table>`;
    } catch {
      const target = host.querySelector<HTMLElement>("[data-role='history-list']");
      if (target) target.innerHTML = `<div class="qb-sent-stamp__empty">Could not load the history.</div>`;
    }
  }

  // Refresh when the server copy changes (an autosave landed), the quote
  // switches, or the status moves. Keystrokes notify too, but don't change the key.
  const offStore = store.subscribe((q) => {
    const key = `${q.serverId || ""}|${q.serverVersion ?? ""}|${q.status}`;
    if (key === lastKey) return;
    lastKey = key;
    schedule();
  });

  // The send snapshot is written just after /quotes/send replies, so look
  // again shortly after a send, and once more in case the server was slow.
  const onSent = (): void => {
    followUps.push(window.setTimeout(() => void refresh(), 1500));
    followUps.push(window.setTimeout(() => void refresh(), 5000));
  };
  window.addEventListener("qb:quote-sent", onSent);

  const q0 = store.get();
  lastKey = `${q0.serverId || ""}|${q0.serverVersion ?? ""}|${q0.status}`;
  void refresh();

  return () => {
    destroyed = true;
    offStore();
    window.removeEventListener("qb:quote-sent", onSent);
    if (timer !== null) window.clearTimeout(timer);
    followUps.forEach((t) => window.clearTimeout(t));
    host.innerHTML = "";
  };
}
